import { CloudIcon } from '@heroicons/react/24/outline';
import { useMemo } from 'react';

import { useDateFormatter } from '@/hooks/useDateFormatter';
import { useTranslation } from '@/hooks/useTranslation';
import { getInjector, getRootScope } from '@/services/angularjs-bridge';

import { Badge } from '../../core/Badge';
import { IconButton } from '../../core/IconButton';
import HoverPopover from '../hoverPopover';
import { TooltipItem, TooltipList } from './actionTooltipList';

const checkItemRepublication = (): boolean => {
    try {
        const injector = getInjector();
        if (!injector) return false;

        const $rootScope = getRootScope(injector);
        const customItemListSettings = $rootScope?.getService?.('CustomItemListSettings');
        const isPublicationDirtyIconEnabled = customItemListSettings?.enablePublicationDirtyIcon?.() ?? false;

        return Boolean(
            isPublicationDirtyIconEnabled
            || $rootScope?.hasSettingFeature?.('SHOW_DIRTY_PUBLICATIONS')
            || $rootScope?.systemSettings?.UI_SHOW_ITEM_REPUBLISH_NEEDED_ICON
        );
    } catch {
        return false;
    }
};

export const PublicationPopover = ({ item }: { item: any }) => {
    const { formatDate } = useDateFormatter();
    const { t } = useTranslation();

    const republicationEnabled = checkItemRepublication();
    const isDirty = item.publications_dirty__ != null;
    const showDirtyBadge = republicationEnabled && isDirty;

    const contentItems = useMemo((): TooltipItem[] => {
        const publishedOn = `${t('PUBLICATIONS.ON')} ${formatDate(item.publishedAt__, 'medium')}`;

        if (showDirtyBadge) {
            return [{ key: 'publication-dirty', text: t('PUBLICATIONS.DIRTY'), severity: 'error' }];
        }

        return [{ key: 'publication-on', text: publishedOn, severity: 'success' }];
    }, [formatDate, item.publishedAt__, showDirtyBadge, t]);

    const trigger = (
        <div className="tw-relative">
            <IconButton variant="lighter-gray" icon={CloudIcon} size="lg" className={'tw-py-[10px] ' + (showDirtyBadge ? 'tw-pr-[15px]' : '')}/>
            {showDirtyBadge &&
            <Badge variant="error" type='count' className='tw-absolute tw-right-0 tw-top-0 tw-mt-[4px]' data-publication-badge>!</Badge>}
        </div>
    );

    return <HoverPopover triggerContent={trigger}><TooltipList items={contentItems} /></HoverPopover>;
};
